import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Gift, Plus, Edit2, Trash2, X, Flag, Calendar, Repeat } from 'lucide-react';
import './HolidayManagement.css';

interface Holiday {
  id: number;
  name: string;
  startDate: string;
  endDate?: string | null;
  year: number;
  isRecurring: boolean;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export const HolidayManagement: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    isRecurring: false
  });

  // German holidays template mit Zeiträumen
  const germanHolidays = [
    { name: 'Neujahr', startDate: `${selectedYear}-01-01`, isRecurring: true },
    { name: 'Tag der Arbeit', startDate: `${selectedYear}-05-01`, isRecurring: true },
    { name: 'Tag der Deutschen Einheit', startDate: `${selectedYear}-10-03`, isRecurring: true },
    { name: 'Weihnachten', startDate: `${selectedYear}-12-25`, endDate: `${selectedYear}-12-26`, isRecurring: true },
    { name: 'Silvester', startDate: `${selectedYear}-12-31`, isRecurring: true }
  ];

  useEffect(() => {
    loadHolidays();
  }, [selectedYear]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getHolidays(selectedYear);
      setHolidays(response.data.holidays);
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Laden der Feiertage' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingHoliday) {
        await adminAPI.updateHoliday(editingHoliday.id, formData);
        setMessage({ type: 'success', text: 'Feiertag erfolgreich aktualisiert' });
        setEditingHoliday(null);
      } else {
        await adminAPI.createHoliday(formData);
        setMessage({ type: 'success', text: 'Feiertag erfolgreich erstellt' });
      }
      
      setFormData({ name: '', startDate: '', endDate: '', description: '', isRecurring: false });
      setShowModal(false);
      loadHolidays();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Speichern des Feiertags'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      startDate: holiday.startDate.split('T')[0],
      endDate: holiday.endDate ? holiday.endDate.split('T')[0] : '',
      description: holiday.description || '',
      isRecurring: holiday.isRecurring
    });
    setShowModal(true);
  };

  const handleRowClick = (holiday: Holiday) => {
    handleEdit(holiday);
  };

  const handleCreate = () => {
    setEditingHoliday(null);
    setFormData({ name: '', startDate: '', endDate: '', description: '', isRecurring: false });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ name: '', startDate: '', endDate: '', description: '', isRecurring: false });
  };

  const addGermanHolidaysForYear = async () => {
    if (!confirm(`Deutsche Standardfeiertage für ${selectedYear} hinzufügen?`)) return;

    try {
      setLoading(true);
      for (const holiday of germanHolidays) {
        if (holiday.startDate) {
          try {
            await adminAPI.createHoliday({
              name: holiday.name,
              startDate: holiday.startDate,
              endDate: holiday.endDate || null,
              isRecurring: holiday.isRecurring
            });
          } catch (error) {
            console.log(`Holiday ${holiday.name} already exists or error occurred`);
          }
        }
      }
      setMessage({ 
        type: 'success', 
        text: 'Deutsche Feiertage hinzugefügt (bereits vorhandene wurden übersprungen, bewegliche Feiertage müssen manuell hinzugefügt werden)' 
      });
      loadHolidays();
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Hinzufügen der Feiertage' });
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (startDate: string, endDate?: string | null) => {
    const start = new Date(startDate).toLocaleDateString('de-DE');
    if (endDate) {
      const end = new Date(endDate).toLocaleDateString('de-DE');
      return `${start} - ${end}`;
    }
    return start;
  };

  const getDuration = (startDate: string, endDate?: string | null) => {
    if (!endDate) return '1 Tag';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return `${diffDays} Tag${diffDays > 1 ? 'e' : ''}`;
  };

  const handleDelete = async (holiday: Holiday) => {
    if (!confirm(`Feiertag "${holiday.name}" wirklich löschen?`)) return;

    try {
      await adminAPI.deleteHoliday(holiday.id);
      setMessage({ type: 'success', text: 'Feiertag erfolgreich gelöscht' });
      loadHolidays();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Löschen des Feiertags'
      });
    }
  };

  return (
    <div className="holiday-management">
      <div className="holiday-header">
        <h2>
          <Gift size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Feiertage verwalten
        </h2>
        
        <div className="header-controls">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="year-select"
          >
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() - 2 + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
          
          <button 
            className="btn secondary"
            onClick={addGermanHolidaysForYear}
            disabled={loading}
          >
            <Flag size={16} style={{ marginRight: '4px' }} />
            Deutsche Feiertage hinzufügen
          </button>
          
          <button 
            className="btn primary"
            onClick={handleCreate}
          >
            <Plus size={16} style={{ marginRight: '4px' }} />
            Feiertag hinzufügen
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      <div className="holidays-table-container">
        <div className="holidays-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Zeitraum</th>
                <th>Dauer</th>
                <th>Wochentage</th>
                <th>Wiederkehrend</th>
                <th>Beschreibung</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map(holiday => (
                <tr 
                  key={holiday.id} 
                  className={!holiday.isActive ? 'inactive' : ''}
                  onClick={() => handleRowClick(holiday)}
                  style={{ cursor: 'pointer' }}
                  title="Klicken zum Bearbeiten"
                >
                  <td><strong>{holiday.name}</strong></td>
                  <td>{formatDateRange(holiday.startDate, holiday.endDate)}</td>
                  <td>
                    <span className="duration-badge">
                      {getDuration(holiday.startDate, holiday.endDate)}
                    </span>
                  </td>
                  <td>
                    <div className="weekdays">
                      {holiday.endDate ? (
                        <>
                          {new Date(holiday.startDate).toLocaleDateString('de-DE', { weekday: 'short' })}
                          {' - '}
                          {new Date(holiday.endDate).toLocaleDateString('de-DE', { weekday: 'short' })}
                        </>
                      ) : (
                        new Date(holiday.startDate).toLocaleDateString('de-DE', { weekday: 'long' })
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`recurring-badge ${holiday.isRecurring ? 'recurring' : 'single'}`}>
                      {holiday.isRecurring ? <Repeat size={16} /> : <Calendar size={16} />}
                    </span>
                  </td>
                  <td>{holiday.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${holiday.isActive ? 'active' : 'inactive'}`}>
                      {holiday.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn small primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(holiday);
                      }}
                      title="Bearbeiten"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn small danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(holiday);
                      }}
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {holidays.length === 0 && !loading && (
            <div className="empty-state">
              <p>Keine Feiertage für {selectedYear} gefunden.</p>
              <button 
                className="btn primary"
                onClick={handleCreate}
              >
                Ersten Feiertag hinzufügen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content holiday-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingHoliday ? (
                  <>
                    <Edit2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Feiertag bearbeiten
                  </>
                ) : (
                  <>
                    <Plus size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Neuer Feiertag
                  </>
                )}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="z.B. Neujahr oder Betriebsferien"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isRecurring}
                        onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                      />
                      Jährlich wiederkehrend
                    </label>
                    <small>Wird automatisch für jedes Jahr angezeigt</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Startdatum:</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Enddatum (optional):</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      min={formData.startDate}
                    />
                    <small>Für Zeiträume wie Betriebsferien</small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Beschreibung (optional):</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="z.B. Gesetzlicher Feiertag, Betriebsferien"
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn secondary" onClick={closeModal}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn primary" disabled={loading}>
                    {loading ? 'Speichern...' : (editingHoliday ? 'Aktualisieren' : 'Hinzufügen')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
